/**
 * BulkPaymentsPage — Shows results for bulk payment ID search.
 * Reads IDs from query param `?ids=ID1,ID2,...`, fetches via POST /payments/fetch,
 * and displays results in a paginated table.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Button from '@cloudscape-design/components/button';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { fetchBulkPayments, clearBulkPayments } from 'src/store/slices/payment/paymentSlice';
import {
  PAYMENT_COLUMN_DEFINITIONS,
  PAYMENT_VISIBLE_CONTENT_OPTIONS,
  PAGE_SIZE_OPTIONS,
  DEFAULT_VISIBLE_COLUMNS,
  DEFAULT_PAGE_SIZE,
} from './filePaymentsColumns';

export const BulkPaymentsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const paymentIds = useMemo(() => {
    const raw = searchParams.get('ids') ?? '';
    return raw.split(',').map(id => id.trim()).filter(Boolean);
  }, [searchParams]);

  const { bulkData, bulkLoading, bulkError } = useAppSelector(state => state.payment);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);

  useEffect(() => {
    if (paymentIds.length === 0) return;
    dispatch(fetchBulkPayments(paymentIds.slice(0, 100)));
    return () => { dispatch(clearBulkPayments()); };
  }, [paymentIds, dispatch]);

  const handleRefresh = () => {
    if (paymentIds.length > 0) dispatch(fetchBulkPayments(paymentIds.slice(0, 100)));
  };

  const allPayments = bulkData?.payments ?? [];
  const notFound = bulkData?.notFound ?? [];
  const totalCount = allPayments.length;
  const pagesCount = Math.max(1, Math.ceil(totalCount / pageSize));

  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return allPayments.slice(start, start + pageSize);
  }, [allPayments, currentPage, pageSize]);

  const handlePageChange = ({ detail }: { detail: { currentPageIndex: number } }) => {
    setCurrentPage(detail.currentPageIndex);
  };

  const handlePreferencesConfirm = ({ detail }: { detail: { pageSize?: number; visibleContent?: readonly string[] } }) => {
    if (detail.pageSize && detail.pageSize !== pageSize) {
      setPageSize(detail.pageSize);
      setCurrentPage(1);
    }
    if (detail.visibleContent) {
      setVisibleColumns([...detail.visibleContent]);
    }
  };

  const activeColumns = PAYMENT_COLUMN_DEFINITIONS.filter(col => visibleColumns.includes(col.id));

  if (paymentIds.length === 0) {
    return (
      <Box padding="l">
        <SpaceBetween size="l">
          <BreadcrumbGroup items={[{ text: 'Search', href: '/' }, { text: 'Bulk Payments', href: '#' }]} onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }} />
          <Box textAlign="center" color="text-body-secondary">No payment IDs provided.</Box>
        </SpaceBetween>
      </Box>
    );
  }

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[{ text: 'Search', href: '/' }, { text: `Bulk Payments (${paymentIds.length} queried)`, href: '#' }]}
          onFollow={(e) => { e.preventDefault(); if (e.detail.href !== '#') navigate(e.detail.href); }}
        />

        {bulkLoading && !bulkData && (
          <Box textAlign="center" padding={{ vertical: 'xl' }}>
            <SpaceBetween size="s" alignItems="center">
              <Spinner size="large" />
              <Box color="text-body-secondary">Loading {paymentIds.length} payments…</Box>
            </SpaceBetween>
          </Box>
        )}

        {(bulkData || bulkError) && (
          <Table
            variant="container"
            resizableColumns
            sortingDisabled
            loading={bulkLoading}
            loadingText="Loading payments…"
            items={pagedItems}
            columnDefinitions={activeColumns}
            header={
              <Header
                variant="h2"
                counter={`(${totalCount} found${notFound.length > 0 ? `, ${notFound.length} not found` : ''})`}
                actions={
                  <Button iconName="refresh" onClick={handleRefresh} loading={bulkLoading}>
                    Refresh
                  </Button>
                }
              >
                Bulk Payment Results
              </Header>
            }
            empty={
              <Box textAlign="center" padding={{ vertical: 'l' }}>
                <SpaceBetween size="s">
                  <Box color="text-body-secondary">
                    {bulkError ?? 'No payments found for the provided IDs.'}
                  </Box>
                  {bulkError && <Button onClick={handleRefresh}>Retry</Button>}
                </SpaceBetween>
              </Box>
            }
            pagination={
              <Pagination
                currentPageIndex={currentPage}
                pagesCount={pagesCount}
                onChange={handlePageChange}
              />
            }
            preferences={
              <CollectionPreferences
                title="Preferences"
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                preferences={{ pageSize, visibleContent: visibleColumns }}
                onConfirm={handlePreferencesConfirm}
                pageSizePreference={{ title: 'Page size', options: PAGE_SIZE_OPTIONS }}
                visibleContentPreference={{
                  title: 'Visible columns',
                  options: [{ label: 'Payment properties', options: PAYMENT_VISIBLE_CONTENT_OPTIONS }],
                }}
              />
            }
          />
        )}
      </SpaceBetween>
    </Box>
  );
};
